"use client";

import { useState } from "react";
import Link from "next/link";
import {
  QUALIFICATIONS_TREE,
  ROLES_TREE,
  findLabelById,
} from "@/lib/taxonomy";
import type { CourseRow } from "@/app/actions/courses";
import type { UserRole } from "@/lib/auth";

function getFormatLabel(f: string): string {
  switch (f) {
    case "online":
      return "Online";
    case "in-person":
      return "In Person";
    case "both":
      return "Online & In Person";
    default:
      return f;
  }
}

function getSeasonLabel(st: string): string {
  switch (st) {
    case "winter":
      return "Winter";
    case "summer":
      return "Summer";
    case "festivals":
      return "Festivals";
    default:
      return st;
  }
}

function getSeasonBadgeStyle(st: string): string {
  switch (st) {
    case "winter":
      return "bg-[#E8EBF0] text-[#1B2A4A]";
    case "summer":
      return "bg-[#FDF8EE] text-gold border border-[#F0E6D0]";
    case "festivals":
      return "bg-[#FDF0EB] text-[#E8835C]";
    default:
      return "bg-[#F0F0F0] text-text-body";
  }
}

export function CourseDetailContent({
  course,
  candidateQualifications,
  userRole,
}: {
  course: CourseRow;
  candidateQualifications: string[];
  userRole?: UserRole;
}) {
  const [showShare, setShowShare] = useState(false);

  const qualLabels = course.qualifications
    .map((q) => ({ id: q, label: findLabelById(QUALIFICATIONS_TREE, q) }))
    .filter((q) => q.label);
  const roleLabels = course.roles
    .map((r) => ({ id: r, label: findLabelById(ROLES_TREE, r) }))
    .filter((r) => r.label);

  // Check if candidate already has any of these qualifications
  const candidateQualSet = new Set(candidateQualifications);
  const alreadyHas = course.qualifications.filter((q) => candidateQualSet.has(q));
  const doesntHave = course.qualifications.filter((q) => !candidateQualSet.has(q));

  const courseUrl = `https://peakwave.co/plan/courses/${course.id}`;

  return (
    <div className="max-w-[800px] mx-auto px-5 md:px-10 pb-20">
      {/* Back link */}
      <div className="pt-6 pb-4">
        <Link
          href="/plan/courses"
          className="text-[13px] text-text-muted no-underline hover:text-gold transition-colors inline-flex items-center gap-1"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          All Courses
        </Link>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {course.season_types.map((st) => (
            <span
              key={st}
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${getSeasonBadgeStyle(st)}`}
            >
              {getSeasonLabel(st)}
            </span>
          ))}
          {course.featured && (
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gold/15 text-gold border border-gold/25">
              Featured
            </span>
          )}
        </div>

        <h1 className="font-serif text-[28px] sm:text-[36px] text-navy leading-[1.1] mb-2">
          {course.title}
        </h1>
        <p className="text-[15px] text-text-muted">
          by{" "}
          {course.provider_url ? (
            <a
              href={course.provider_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold no-underline hover:underline font-medium"
            >
              {course.provider_name}
            </a>
          ) : (
            <span className="font-medium">{course.provider_name}</span>
          )}
        </p>
      </div>

      {/* Main content grid */}
      <div className="grid md:grid-cols-[1fr_280px] gap-8 md:gap-10">
        {/* Left: Description + Details */}
        <div>
          {/* Candidate qualification notice */}
          {userRole === "candidate" && alreadyHas.length > 0 && doesntHave.length === 0 && (
            <div className="mb-6 px-4 py-3 rounded-[10px] bg-[#E8F5E9] border border-[#C8E6C9]">
              <p className="text-[14px] text-success font-medium flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                You already have this qualification on your profile
              </p>
            </div>
          )}

          {userRole === "candidate" && doesntHave.length > 0 && (
            <div className="mb-6 px-4 py-3 rounded-[10px] bg-gold/5 border border-gold/20">
              <p className="text-[14px] text-navy font-medium mb-1">
                This course can add to your profile:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {doesntHave.map((qId) => {
                  const label = findLabelById(QUALIFICATIONS_TREE, qId);
                  return label ? (
                    <span key={qId} className="px-2 py-0.5 rounded-[5px] bg-gold/10 text-gold text-[12px] font-medium">
                      {label}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {/* Description */}
          {course.description && (
            <div className="mb-8">
              <h2 className="font-serif text-[20px] text-navy mb-3">About this course</h2>
              <p className="text-[15px] text-text-body leading-[1.7] whitespace-pre-line">
                {course.description}
              </p>
            </div>
          )}

          {/* Qualifications awarded */}
          {qualLabels.length > 0 && (
            <div className="mb-8">
              <h2 className="font-serif text-[20px] text-navy mb-3">Qualifications you&apos;ll get</h2>
              <div className="flex flex-wrap gap-2">
                {qualLabels.map((q) => (
                  <span
                    key={q.id}
                    className="px-3 py-1.5 rounded-sm bg-[#E8F5E9] text-success text-[13px] font-medium"
                  >
                    {q.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Roles this prepares you for */}
          {roleLabels.length > 0 && (
            <div className="mb-8">
              <h2 className="font-serif text-[20px] text-navy mb-3">Prepares you for</h2>
              <div className="flex flex-wrap gap-2">
                {roleLabels.map((r) => (
                  <Link
                    key={r.id}
                    href={`/jobs?role=${r.id}`}
                    className="px-3 py-1.5 rounded-sm bg-navy/[0.06] text-navy text-[13px] font-medium no-underline hover:bg-navy/10 transition-colors"
                  >
                    {r.label} &rarr;
                  </Link>
                ))}
              </div>
              <p className="text-[12px] text-text-muted mt-2">
                Click a role to see matching job listings
              </p>
            </div>
          )}
        </div>

        {/* Right: Sidebar */}
        <div>
          <div className="bg-[#FFFDF8] border border-[#E8E2D6] rounded-[14px] p-5 sticky top-24">
            {/* Price */}
            {course.price_from != null && (
              <div className="mb-4">
                <p className="text-[12px] text-text-muted uppercase tracking-wide mb-0.5">
                  From
                </p>
                <p className="text-[24px] font-bold text-navy">
                  &pound;{course.price_from.toLocaleString()}
                </p>
              </div>
            )}

            {/* Key details */}
            <div className="space-y-3 mb-5">
              <DetailRow
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                }
                label="Duration"
                value={course.duration ?? "Contact provider"}
              />
              <DetailRow
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    {course.format === "online" ? (
                      <>
                        <rect x="2" y="3" width="20" height="14" rx="2" />
                        <line x1="8" y1="21" x2="16" y2="21" />
                        <line x1="12" y1="17" x2="12" y2="21" />
                      </>
                    ) : (
                      <>
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </>
                    )}
                  </svg>
                }
                label="Format"
                value={getFormatLabel(course.format)}
              />
              {course.location && (
                <DetailRow
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  }
                  label="Location"
                  value={course.location}
                />
              )}
            </div>

            {/* CTA */}
            {course.booking_url ? (
              <a
                href={course.booking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 bg-gold text-white rounded-sm text-[15px] font-semibold text-center no-underline font-sans transition-all hover:bg-gold-light hover:-translate-y-0.5 shadow-[0_4px_16px_rgba(28, 138, 153,0.25)]"
              >
                View Course &rarr;
              </a>
            ) : (
              <p className="text-[13px] text-text-muted text-center">
                Contact the provider directly for booking details
              </p>
            )}

            {/* Share */}
            <div className="mt-4 pt-4 border-t border-[#F0EDE6]">
              <div className="relative">
                <button
                  onClick={() => setShowShare(!showShare)}
                  className="text-[13px] text-text-muted hover:text-gold transition-colors bg-transparent border-none cursor-pointer font-sans inline-flex items-center gap-1.5"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  Share this course
                </button>
                {showShare && (
                  <div className="absolute bottom-[calc(100%+8px)] left-0 bg-white rounded-md shadow-[0_8px_30px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.04)] p-3 min-w-[200px] z-10">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(courseUrl);
                        setShowShare(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-[13px] text-text-body hover:bg-warm-bg rounded-sm transition-colors bg-transparent border-none cursor-pointer font-sans"
                    >
                      Copy link
                    </button>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`${course.title} - ${courseUrl}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-3 py-2 text-[13px] text-text-body hover:bg-warm-bg rounded-sm transition-colors no-underline"
                    >
                      WhatsApp
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Browse more CTA */}
      <div className="mt-12 pt-8 border-t border-border">
        <h2 className="font-serif text-[22px] text-navy mb-3">
          Looking for more courses?
        </h2>
        <p className="text-[14px] text-text-muted mb-4 max-w-[500px]">
          Browse all available courses and qualifications, or search for
          something specific.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            href="/plan/courses"
            className="bg-gold text-white px-6 py-2.5 rounded-sm text-[14px] font-semibold no-underline font-sans transition-all hover:bg-gold-light"
          >
            Browse All Courses
          </Link>
          <Link
            href="/jobs"
            className="text-navy px-6 py-2.5 rounded-sm text-[14px] font-semibold no-underline font-sans transition-all border-[1.5px] border-navy/18 hover:border-navy hover:bg-navy/[0.03]"
          >
            Browse Jobs
          </Link>
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-text-muted shrink-0 mt-0.5">{icon}</span>
      <div>
        <p className="text-[11px] text-text-muted uppercase tracking-wide">
          {label}
        </p>
        <p className="text-[14px] text-navy font-medium">{value}</p>
      </div>
    </div>
  );
}
